import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { useShallow } from "zustand/react/shallow";
import type { AnchorType } from "../services/cmTables";
import type {
  INormalisedRelations,
  RelationshipEditorItem,
  RelationshipEditorState,
} from "../types";
import { initRelationshipEditorState } from "../types";
import { actionNameBuilder } from "../utilities/storeActionNameBuilder";
import { applyResetMiddleware } from "./middleware/resetMiddleware";

const action = actionNameBuilder("relationshipEditor");

// ─── Dialog State ─────────────────────────────────────────────────────────────
// Used by RelationshipEditDialog (Entity Form context).
// Single anchor being edited at a time.

interface DialogState {
  anchorId: number | null;
  anchorType: AnchorType;
  /** Snapshot of relationships as loaded — diffed against `editorState` on save to build the PATCH payload. */
  originalState: RelationshipEditorState;
  editorState: RelationshipEditorState;
}

// ─── Editor Page State ────────────────────────────────────────────────────────
// Used by the Editor Page context.
// Multiple rows; only dirty rows get a PUT on save.

interface EditorPageState {
  anchorType: AnchorType;
  /** Snapshot of relationships as loaded per row id — diffed against `drafts` on save to build the PATCH payload. */
  originals: Record<number, RelationshipEditorState>;
  /** Working draft per row id. */
  drafts: Record<number, RelationshipEditorState>;
  /** Row ids that have unsaved changes. */
  dirtyRows: number[];
}

// ─── Full Store ───────────────────────────────────────────────────────────────

interface RelationshipEditorStoreState {
  dialog: DialogState;
  editorPage: EditorPageState;

  actions: {
    // ── Dialog actions ──────────────────────────────────────────────────────

    /**
     * Call when RelationshipEditDialog opens. Seeds `originalState` from the row's
     * server relationships, used as the diff baseline. `editorState` defaults to the
     * same snapshot, or the provided `editorState` (e.g. to resume from unsaved
     * relationship changes made in a previous dialog session).
     */
    initDialog: (
      anchorId: number,
      anchorType: AnchorType,
      relationships: INormalisedRelations,
      editorState?: RelationshipEditorState
    ) => void;

    /** Update the item list for one table slug (or "media") in the dialog editor. */
    setDialogTableItems: (
      tableSlug: string,
      items: RelationshipEditorItem[]
    ) => void;

    /** Reset dialog state (call on close). */
    resetDialog: () => void;

    // ── Editor page actions ─────────────────────────────────────────────────

    /** Call on page/endpoint load. Seeds drafts from all rows. */
    initEditorPage: (
      anchorType: AnchorType,
      rows: Array<{ id: number; relationships: INormalisedRelations }>
    ) => void;

    /** Update the item list for one table slug in a specific row's draft. Marks row dirty. */
    setDraftTableItems: (
      rowId: number,
      tableSlug: string,
      items: RelationshipEditorItem[]
    ) => void;

    /** Clear dirty flag for a row after a successful save. */
    markRowSaved: (rowId: number) => void;

    /**
     * Re-baselines a single row's `originals` and `drafts` from freshly-fetched server
     * relationships (which carry real `relationship_id`s for newly-created relationships)
     * and clears its dirty flag. Call after a successful PATCH + refetch so the next
     * diff doesn't re-send already-applied creates/deletes.
     */
    reinitRow: (rowId: number, relationships: INormalisedRelations) => void;

    /** Reset editor page state (call when leaving the page or switching endpoint). */
    resetEditorPage: () => void;
  };
}

const INITIAL_DIALOG_STATE: DialogState = {
  anchorId: null,
  anchorType: "cmtabledata",
  originalState: {},
  editorState: {},
};

const INITIAL_EDITOR_PAGE_STATE: EditorPageState = {
  anchorType: "cmtabledata",
  originals: {},
  drafts: {},
  dirtyRows: [],
};

export const useRelationshipEditorStore = create<RelationshipEditorStoreState>()(
  devtools(
    immer(
      applyResetMiddleware(
        (set) => ({
          dialog: { ...INITIAL_DIALOG_STATE },
          editorPage: { ...INITIAL_EDITOR_PAGE_STATE },

          actions: {
            // ── Dialog ────────────────────────────────────────────────────────

            initDialog: (anchorId, anchorType, relationships, editorState) =>
              set(
                (state) => {
                  const initial = initRelationshipEditorState(relationships);
                  state.dialog.anchorId = anchorId;
                  state.dialog.anchorType = anchorType;
                  state.dialog.originalState = initial;
                  state.dialog.editorState = editorState ?? initial;
                },
                false,
                action("initDialog")
              ),

            setDialogTableItems: (tableSlug, items) =>
              set(
                (state) => {
                  state.dialog.editorState[tableSlug] = items;
                },
                false,
                action("setDialogTableItems")
              ),

            resetDialog: () =>
              set(
                (state) => {
                  state.dialog = { ...INITIAL_DIALOG_STATE };
                },
                false,
                action("resetDialog")
              ),

            // ── Editor page ───────────────────────────────────────────────────

            initEditorPage: (anchorType, rows) =>
              set(
                (state) => {
                  state.editorPage.anchorType = anchorType;
                  state.editorPage.dirtyRows = [];
                  state.editorPage.originals = {};
                  state.editorPage.drafts = {};
                  for (const row of rows) {
                    const initial = initRelationshipEditorState(row.relationships);
                    state.editorPage.originals[row.id] = initial;
                    state.editorPage.drafts[row.id] = initial;
                  }
                },
                false,
                action("initEditorPage")
              ),

            setDraftTableItems: (rowId, tableSlug, items) =>
              set(
                (state) => {
                  if (!state.editorPage.drafts[rowId]) {
                    state.editorPage.drafts[rowId] = {};
                  }
                  state.editorPage.drafts[rowId][tableSlug] = items;
                  if (!state.editorPage.dirtyRows.includes(rowId)) {
                    state.editorPage.dirtyRows.push(rowId);
                  }
                },
                false,
                action("setDraftTableItems")
              ),

            markRowSaved: (rowId) =>
              set(
                (state) => {
                  state.editorPage.dirtyRows = state.editorPage.dirtyRows.filter(
                    (id) => id !== rowId
                  );
                },
                false,
                action("markRowSaved")
              ),

            reinitRow: (rowId, relationships) =>
              set(
                (state) => {
                  const initial = initRelationshipEditorState(relationships);
                  state.editorPage.originals[rowId] = initial;
                  state.editorPage.drafts[rowId] = initial;
                  state.editorPage.dirtyRows = state.editorPage.dirtyRows.filter(
                    (id) => id !== rowId
                  );
                },
                false,
                action("reinitRow")
              ),

            resetEditorPage: () =>
              set(
                (state) => {
                  state.editorPage = { ...INITIAL_EDITOR_PAGE_STATE };
                },
                false,
                action("resetEditorPage")
              ),
          },
        }),
        "relationshipEditor"
      )
    ),
    { name: "RelationshipEditorStore" }
  )
);

// ─── Selectors ────────────────────────────────────────────────────────────────

export const useRelationshipEditorActions = () =>
  useRelationshipEditorStore((s) => s.actions);

export const useDialogEditorState = () =>
  useRelationshipEditorStore(useShallow((s) => s.dialog));

export const useEditorPageState = () =>
  useRelationshipEditorStore(useShallow((s) => s.editorPage));
